import { useMemo, useState, useEffect, useRef } from 'react';
import { useUserLanguage, useCreateCommunicationContext } from '@/customHooks';
import textsData from '@/textData/CreateCommunicationsTexts.json';
import { LoadingSpinner, SquareButton2, RowSpacer } from '../atoms';
import { Pagination } from '../molecules';
import { NormalFormTextField } from '../molecules/NormalFormFields';
import {
  useGetTenantsForContractsQuery,
  useGetTenantsForContractsLazyQuery,
  useGetOwnersQuery,
  useGetSuppliersQuery,
  useGetPropertiesForContractsQuery,
  useGetPropertyLazyQuery,
} from '@/graphql/generated';
import { OrderDirectionOption, PageSizeOption } from '@/graphql/generated';
import type { GetPropertyQuery } from '@/graphql/generated';
import { capitalizeFirstLetter } from '@/helpers';
import groupsIcon from '@/assets/communication-icons/groups.png';
import allGroupsIcon from '@/assets/communication-icons/all-groups.png';
import { PropertyMultiSelectionList } from './PropertyMultiSelectionList';

type UserForSelection = { id: string; fullName: string; userType: 'TENANT' | 'OWNER' | 'SUPPLIER' };

type PropertyDetail = NonNullable<GetPropertyQuery['property']>;

function collectUsersFromPropertyDetail(property: PropertyDetail): UserForSelection[] {
  const list: UserForSelection[] = [];
  if (property.owner?.user?.id) {
    list.push({
      id: property.owner.user.id,
      fullName: `${capitalizeFirstLetter(property.owner.firstName)} ${capitalizeFirstLetter(property.owner.lastName)}`,
      userType: 'OWNER',
    });
  }
  property.unities?.forEach((u) => {
    u.contracts?.forEach((c) => {
      const tenant = c.tenant;
      const userId = tenant?.user?.id;
      if (userId && tenant) {
        list.push({
          id: userId,
          fullName: `${capitalizeFirstLetter(tenant.firstName)} ${capitalizeFirstLetter(tenant.lastName)}`,
          userType: 'TENANT',
        });
      }
    });
  });
  property.incidents?.forEach((inc) => {
    const supplier = inc.supplier;
    const userId = supplier?.user?.id;
    if (userId && supplier) {
      list.push({
        id: userId,
        fullName: `${capitalizeFirstLetter(supplier.firstName)} ${capitalizeFirstLetter(supplier.lastName)}`,
        userType: 'SUPPLIER',
      });
    }
  });
  return list;
}

export const SelectCommunicationAudience = () => {
  const userLanguage = useUserLanguage();
  const texts = userLanguage === 'es' ? textsData.es : textsData.en;
  const {
    state,
    setTargetUserTypes,
    setSelectedUserIds,
    toggleUserSelection,
    setAllUsersSelected,
    setButtonDisabled,
    setButtonEnabled,
    setSelectedPropertyIds,
    setAllStakeholdersOnSelectedProperties,
    setOnlyActiveContractsSelected,
    toggleExcludeRecipientUser,
    setExcludeRecipientUserIds,
  } = useCreateCommunicationContext();

  const activeType = state.targetUserTypes[0] ?? null;

  const [searchUser, setSearchUser] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const tenantsVariables = useMemo(
    () => ({
      input: {
        search: searchUser || undefined,
        page: '1',
        pageSize: PageSizeOption.SeventyFive,
        orderDirection: OrderDirectionOption.Asc,
      },
    }),
    [searchUser],
  );

  const audienceSelectionReady = true;

  const { data: tenantsData } = useGetTenantsForContractsQuery({
    variables: tenantsVariables,
    fetchPolicy: 'network-only',
    skip: activeType !== 'TENANT' || !audienceSelectionReady,
  });

  const { data: ownersData } = useGetOwnersQuery({
    variables: {
      filterInput: {
        search: searchUser || undefined,
        page: '1',
        pageSize: PageSizeOption.SeventyFive,
        orderDirection: OrderDirectionOption.Asc,
      },
    },
    fetchPolicy: 'network-only',
    skip: activeType !== 'OWNER' || !audienceSelectionReady,
  });

  const suppliersVariables = useMemo(
    () => ({
      filterInput: {
        search: searchUser || undefined,
        page: '1',
        pageSize: PageSizeOption.SeventyFive,
        orderDirection: OrderDirectionOption.Asc,
      },
    }),
    [searchUser],
  );

  const { data: suppliersData } = useGetSuppliersQuery({
    variables: suppliersVariables,
    fetchPolicy: 'network-only',
    skip: activeType !== 'SUPPLIER' || !audienceSelectionReady,
  });
  const [fetchTenants] = useGetTenantsForContractsLazyQuery({
    fetchPolicy: 'network-only',
  });
  const [fetchPropertyDetail] = useGetPropertyLazyQuery({
    fetchPolicy: 'network-only',
  });

  const PROPERTIES_PAGE_SIZE = PageSizeOption.Fifty;
  const [propertyListPage, setPropertyListPage] = useState(1);
  const [propertySearch, setPropertySearch] = useState('');
  const [propertySearchQuery, setPropertySearchQuery] = useState('');

  useEffect(() => {
    const handler = setTimeout(() => {
      setPropertySearchQuery(propertySearch);
    }, 450);
    return () => clearTimeout(handler);
  }, [propertySearch]);

  const propertySearchTrimmed = propertySearchQuery.trim();
  const effectivePropertySearch =
    propertySearchTrimmed.length >= 3 ? propertySearchTrimmed : '';

  const propertiesQueryVariables = useMemo(
    () => ({
      input: {
        page: String(propertyListPage),
        pageSize: PROPERTIES_PAGE_SIZE,
        orderDirection: OrderDirectionOption.Asc,
        ...(effectivePropertySearch ? { search: effectivePropertySearch } : {}),
      },
    }),
    [propertyListPage, effectivePropertySearch],
  );

  const prevPropertySearchKeyRef = useRef(effectivePropertySearch);
  useEffect(() => {
    if (prevPropertySearchKeyRef.current === effectivePropertySearch) return;
    prevPropertySearchKeyRef.current = effectivePropertySearch;
    setPropertyListPage(1);
  }, [effectivePropertySearch]);

  const {
    data: propertiesQueryData,
    loading: loadingPropertiesList,
  } = useGetPropertiesForContractsQuery({
    variables: propertiesQueryVariables,
    fetchPolicy: 'network-only',
  });

  const propertiesForPicker = propertiesQueryData?.properties?.items ?? [];
  const propertyPagination = propertiesQueryData?.properties?.pagination;
  const [excludeCandidateUsers, setExcludeCandidateUsers] = useState<UserForSelection[]>([]);
  const [loadingExcludeUsers, setLoadingExcludeUsers] = useState(false);

  const propertyIdsKey = state.selectedPropertyIds.join('|');
  const excludeIdsRef = useRef(state.excludeRecipientUserIds);
  excludeIdsRef.current = state.excludeRecipientUserIds;

  const [excludeUiMode, setExcludeUiMode] = useState<'all' | 'exclude'>(() =>
    state.excludeRecipientUserIds.length > 0 ? 'exclude' : 'all',
  );

  const prevPropertyIdsKeyRef = useRef(propertyIdsKey);
  useEffect(() => {
    if (prevPropertyIdsKeyRef.current === propertyIdsKey) return;
    prevPropertyIdsKeyRef.current = propertyIdsKey;
    setExcludeUiMode('all');
    setExcludeRecipientUserIds([]);
  }, [propertyIdsKey, setExcludeRecipientUserIds]);

  useEffect(() => {
    const totalPages = propertyPagination?.totalPages ?? 0;
    if (totalPages > 0 && propertyListPage > totalPages) {
      setPropertyListPage(totalPages);
    }
  }, [propertyPagination?.totalPages, propertyListPage]);

  useEffect(() => {
    if (state.selectedPropertyIds.length === 0) {
      setAllStakeholdersOnSelectedProperties(false);
      return;
    }
    setAllStakeholdersOnSelectedProperties(true);
    setTargetUserTypes([]);
    setSelectedUserIds([]);
    setAllUsersSelected(false);
    setOnlyActiveContractsSelected(false);
  }, [
    propertyIdsKey,
    setAllStakeholdersOnSelectedProperties,
    setTargetUserTypes,
    setSelectedUserIds,
    setAllUsersSelected,
    setOnlyActiveContractsSelected,
  ]);

  useEffect(() => {
    if (state.selectedPropertyIds.length === 0) {
      setExcludeCandidateUsers([]);
      setExcludeRecipientUserIds([]);
      setLoadingExcludeUsers(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingExcludeUsers(true);
      const byId = new Map<string, UserForSelection>();
      try {
        for (const pid of state.selectedPropertyIds) {
          const res = await fetchPropertyDetail({ variables: { id: pid } });
          if (cancelled) return;
          if (res.error) continue;
          const p = res.data?.property;
          if (p) {
            try {
              collectUsersFromPropertyDetail(p).forEach((u) => {
                if (!byId.has(u.id)) byId.set(u.id, u);
              });
            } catch {
              /* respuesta parcial o campos null en runtime */
            }
          }
        }
        if (!cancelled) {
          setExcludeCandidateUsers(Array.from(byId.values()));
          setExcludeRecipientUserIds(
            excludeIdsRef.current.filter((id) => byId.has(id)),
          );
        }
      } catch {
        if (!cancelled) {
          setExcludeCandidateUsers([]);
        }
      } finally {
        if (!cancelled) setLoadingExcludeUsers(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Solo cuando cambian las propiedades elegidas; fetchPropertyDetail es estable en Apollo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyIdsKey]);

  const tenants = tenantsData?.tenants?.items ?? [];
  const owners = ownersData?.owners?.items ?? [];
  const suppliers = suppliersData?.suppliers?.items ?? [];

  const usersList: UserForSelection[] = useMemo(() => {
    const list: UserForSelection[] = [];
    if (activeType === 'TENANT') {
      tenants.forEach((t) => {
        const userId = t.user?.id;
        if (userId) {
          list.push({
            id: userId,
            fullName: `${capitalizeFirstLetter(t.firstName)} ${capitalizeFirstLetter(t.lastName)}`,
            userType: 'TENANT',
          });
        }
      });
    }
    if (activeType === 'OWNER') {
      owners.forEach((o) => {
        const userId = o.user?.id;
        if (userId) {
          list.push({
            id: userId,
            fullName: `${capitalizeFirstLetter(o.firstName)} ${capitalizeFirstLetter(o.lastName)}`,
            userType: 'OWNER',
          });
        }
      });
    }
    if (activeType === 'SUPPLIER') {
      const searchLower = searchUser.toLowerCase();
      suppliers.forEach((s: { firstName: string; lastName: string; user?: { id: string } }) => {
        const userId = s.user?.id;
        if (userId) {
          const fullName = `${capitalizeFirstLetter(s.firstName)} ${capitalizeFirstLetter(s.lastName)}`;
          if (!searchLower || fullName.toLowerCase().includes(searchLower)) {
            list.push({ id: userId, fullName, userType: 'SUPPLIER' });
          }
        }
      });
    }
    return list;
  }, [tenants, owners, suppliers, activeType, searchUser]);

  const pageCount = Math.max(1, Math.ceil(usersList.length / pageSize));
  const pagedUsers = usersList.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  useEffect(() => {
    setCurrentPage(1);
    setOnlyActiveContractsSelected(false);
  }, [activeType, searchUser, setOnlyActiveContractsSelected]);

  const hasPropertyScope = state.selectedPropertyIds.length > 0;

  const isValidAudience =
    (hasPropertyScope && state.allStakeholdersOnSelectedProperties) ||
    (!state.allStakeholdersOnSelectedProperties &&
      activeType !== null &&
      (state.allUsersSelected ||
        state.selectedUserIds.length > 0 ||
        (state.onlyActiveContractsSelected &&
          activeType === 'TENANT' &&
          hasPropertyScope)));

  useEffect(() => {
    if (isValidAudience) {
      setButtonEnabled();
    } else {
      setButtonDisabled();
    }
  }, [isValidAudience, setButtonEnabled, setButtonDisabled]);

  const handleToggleUserType = (option: 'TENANT' | 'OWNER' | 'SUPPLIER') => {
    if (option === activeType) return;
    setTargetUserTypes([option]);
    setAllUsersSelected(false);
    setOnlyActiveContractsSelected(false);
  };

  /** "Todos los usuarios" del tipo: envío con recipientUserTypes (sin paginar toda la cartera). */
  const onToggleAllUsersRow = (next: boolean) => {
    setAllUsersSelected(next);
    setOnlyActiveContractsSelected(false);
    setSelectedUserIds([]);
  };

  const onToggleActiveContractsRow = async (next: boolean) => {
    setOnlyActiveContractsSelected(next);
    setAllUsersSelected(false);
    if (next) {
      setSelectedUserIds([]);
      if (hasPropertyScope) {
        /* inquilinos en la(s) propiedad(es): el backend acota por propertyIds + TENANT */
        return;
      }
      await loadTenantUserIdsWithActiveContracts();
      return;
    }
    setSelectedUserIds([]);
  };

  const loadTenantUserIdsWithActiveContracts = async () => {
    const allIds: string[] = [];
    let page = 1;
    let totalPages = 1;

    do {
      const res = await fetchTenants({
        variables: {
          input: {
            page: String(page),
            pageSize: PageSizeOption.SeventyFive,
            orderDirection: OrderDirectionOption.Asc,
            hasActiveContract: true,
          },
        }
      });

      const tenantsResult = res.data?.tenants;
      if (!tenantsResult) break;

      tenantsResult.items.forEach((t: any) => {
        const userId = t.user?.id;
        if (userId) allIds.push(userId);
      });

      if (page === 1) {
        totalPages = tenantsResult.pagination?.totalPages ?? 1;
      }
      page++;
    } while (page <= totalPages);

    setSelectedUserIds(Array.from(new Set(allIds)));
  };

  const showUserTypeStep =
    !state.allStakeholdersOnSelectedProperties && audienceSelectionReady;

  return (
    <>
      <div className="flex w-full justify-center">
        <div className="w-full min-w-0 max-w-full md:w-10/12">
          <div className="mb-6 w-full min-w-0 max-w-full">
            {loadingPropertiesList && (
              <div className="flex justify-center">
                <LoadingSpinner />
              </div>
            )}
            {!loadingPropertiesList && propertyPagination && (
              <>
                {(propertyPagination.totalItems > 0 ||
                  propertySearchTrimmed.length > 0) && (
                  <div className="mb-4 flex justify-center">
                    <input
                      type="text"
                      placeholder={texts.audience.selectProperty.searchPlaceholder}
                      value={propertySearch}
                      onChange={(e) => setPropertySearch(e.target.value)}
                      className="min-w-[320px] w-full max-w-[860px] rounded-md border border-border px-3 py-2.5 text-base"
                    />
                  </div>
                )}
                {propertyPagination.totalItems === 0 && (
                  <p className="text-center text-sm text-neutral-600">
                    {propertySearchTrimmed
                      ? texts.audience.selectProperty.pagination.emptySearch
                      : texts.audience.selectProperty.pagination.empty}
                  </p>
                )}
                {propertyPagination.totalItems > 0 && (
                  <>
                    <div className="flex justify-center">
                      <div className="w-fit">
                        <PropertyMultiSelectionList
                          key={`${effectivePropertySearch}|${propertyListPage}`}
                          properties={propertiesForPicker}
                          initialSelectedIds={state.selectedPropertyIds}
                          selected={setSelectedPropertyIds}
                        />
                      </div>
                    </div>
                    <div className="flex justify-center">
                      <div className="w-fit">
                        <Pagination
                          currentPage={propertyListPage}
                          onPageChange={setPropertyListPage}
                          currentDataCount={propertiesForPicker.length}
                          pageSize={PROPERTIES_PAGE_SIZE}
                          totalPages={propertyPagination.totalPages}
                        />
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {state.selectedPropertyIds.length > 0 && (
        <div className="flex w-full justify-center">
          <div className="w-full min-w-0 max-w-full md:w-10/12">
            <div className="mt-6 rounded-xl bg-[#f5f5f5] p-6">
              <h3
                className="mb-3 text-center font-bold text-text"
                style={{
                  fontSize: '1rem',
                  marginTop: 0,
                  marginBottom: '0.75rem',
                }}
              >
                {texts.audience.excludeRecipients.sectionExclude}
              </h3>
              <div className="mb-6 flex flex-wrap justify-center gap-4">
                <SquareButton2
                  action={() => {
                    setExcludeUiMode('all');
                    setExcludeRecipientUserIds([]);
                  }}
                  selected={excludeUiMode === 'all'}
                >
                  <>
                    <img src={allGroupsIcon} alt="" style={{ width: 48, height: 48 }} />
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 700,
                        textAlign: 'center',
                        lineHeight: 1.25,
                      }}
                    >
                      {texts.audience.excludeRecipients.cardSendAllTitle}
                    </span>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 400,
                        textAlign: 'center',
                        lineHeight: 1.3,
                      }}
                    >
                      {texts.audience.excludeRecipients.cardSendAllSub}
                    </span>
                  </>
                </SquareButton2>
                <SquareButton2
                  action={() => setExcludeUiMode('exclude')}
                  selected={excludeUiMode === 'exclude'}
                >
                  <>
                    <img src={groupsIcon} alt="" style={{ width: 48, height: 48 }} />
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 700,
                        textAlign: 'center',
                        lineHeight: 1.25,
                      }}
                    >
                      {texts.audience.excludeRecipients.cardExclude}
                    </span>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 400,
                        textAlign: 'center',
                        lineHeight: 1.25,
                      }}
                    >
                      {texts.audience.excludeRecipients.cardExcludeSub}
                    </span>
                  </>
                </SquareButton2>
              </div>

              {excludeUiMode === 'exclude' && (
                <div className="mt-4">
                  {loadingExcludeUsers && (
                    <p>{texts.audience.excludeRecipients.loading}</p>
                  )}
                  {!loadingExcludeUsers &&
                    excludeCandidateUsers.length === 0 && (
                      <p>{texts.audience.excludeRecipients.empty}</p>
                    )}
                  {!loadingExcludeUsers && excludeCandidateUsers.length > 0 && (
                    <div className="max-h-[240px] overflow-y-auto">
                      {excludeCandidateUsers.map((user) => (
                        <div
                          key={user.id}
                          className="flex cursor-pointer items-center gap-3 border-b border-border py-3 last:border-b-0"
                          onClick={() => toggleExcludeRecipientUser(user.id)}
                        >
                          <input
                            type="checkbox"
                            checked={state.excludeRecipientUserIds.includes(
                              user.id,
                            )}
                            onChange={() => toggleExcludeRecipientUser(user.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary1/25 font-semibold text-primary1">
                            {user.fullName.charAt(0).toUpperCase()}
                          </div>
                          <span>
                            {user.fullName}{' '}
                            <small style={{ color: '#666' }}>
                              ({user.userType})
                            </small>
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showUserTypeStep && (
        <div className="flex w-full justify-center">
          <div className="w-full min-w-0 max-w-full md:w-10/12">
            <h3 className="mb-3 text-center text-2xl font-bold text-text">
              {texts.audience.userTypes.title}
            </h3>
            <p className="mb-7 text-center text-base text-[#666]">
              {texts.audience.userTypes.description}
            </p>
            <div className="mb-6 flex flex-wrap justify-center gap-4">
              <SquareButton2
                action={() => handleToggleUserType('OWNER')}
                selected={activeType === 'OWNER'}
              >
                <>
                  <img src={groupsIcon} alt="" style={{ width: 48, height: 48 }} />
                  <span>{texts.audience.userTypes.owners}</span>
                </>
              </SquareButton2>
              <SquareButton2
                action={() => handleToggleUserType('TENANT')}
                selected={activeType === 'TENANT'}
              >
                <>
                  <img src={groupsIcon} alt="" style={{ width: 48, height: 48 }} />
                  <span>{texts.audience.userTypes.rentals}</span>
                </>
              </SquareButton2>
              <SquareButton2
                action={() => handleToggleUserType('SUPPLIER')}
                selected={activeType === 'SUPPLIER'}
              >
                <>
                  <img src={groupsIcon} alt="" style={{ width: 48, height: 48 }} />
                  <span>{texts.audience.userTypes.suppliers}</span>
                </>
              </SquareButton2>
            </div>

            {activeType && (
              <div className="mt-6 rounded-xl bg-[#f5f5f5] p-6">
                <NormalFormTextField
                  label={texts.audience.userTypes.searchUserPlaceholder}
                  placeholder={texts.audience.userTypes.searchUserPlaceholder}
                  value={searchUser}
                  onChange={(v) => setSearchUser(v ?? '')}
                />
                <RowSpacer height={2} />
                <div className="max-h-[240px] overflow-y-auto">
                  <div
                    className="flex cursor-pointer items-center gap-3 border-b border-border py-3 last:border-b-0"
                    onClick={() => {
                      onToggleAllUsersRow(!state.allUsersSelected);
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={state.allUsersSelected}
                      onChange={(e) => {
                        onToggleAllUsersRow(e.target.checked);
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary1/25 font-semibold text-primary1">
                      {'?'}
                    </div>
                    <span>{texts.audience.userTypes.allUsers}</span>
                  </div>
                  {activeType === 'TENANT' && (
                    <div
                      className="flex cursor-pointer items-center gap-3 border-b border-border py-3 last:border-b-0"
                      onClick={() => {
                        void onToggleActiveContractsRow(
                          !state.onlyActiveContractsSelected,
                        );
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={state.onlyActiveContractsSelected}
                        onChange={(e) => {
                          void onToggleActiveContractsRow(e.target.checked);
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary1/25 font-semibold text-primary1">
                        {'A'}
                      </div>
                      <span>{texts.audience.userTypes.tenantsWithActiveContracts}</span>
                    </div>
                  )}
                  {pagedUsers.map((user) => (
                    <div
                      key={user.id}
                      className="flex cursor-pointer items-center gap-3 border-b border-border py-3 last:border-b-0"
                      onClick={() => {
                        setOnlyActiveContractsSelected(false);
                        setAllUsersSelected(false);
                        toggleUserSelection(user.id);
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={
                          !state.allUsersSelected &&
                          state.selectedUserIds.includes(user.id)
                        }
                        onChange={() => toggleUserSelection(user.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary1/25 font-semibold text-primary1">
                        {user.fullName.charAt(0).toUpperCase()}
                      </div>
                      <span>{user.fullName}</span>
                    </div>
                  ))}
                </div>
                {pageCount > 1 && (
                  <>
                    <RowSpacer height={1} />
                    <div className="flex justify-center">
                      <div className="w-fit">
                        {Array.from({ length: pageCount }).map((_, index) => {
                          const pageNumber = index + 1;
                          const isActive = pageNumber === currentPage;
                          return (
                            <button
                              key={pageNumber}
                              type="button"
                              onClick={() => setCurrentPage(pageNumber)}
                              style={{
                                margin: '0 4px',
                                padding: '4px 8px',
                                borderRadius: 4,
                                border: isActive ? '1px solid #00183A' : '1px solid transparent',
                                background: isActive ? '#00183A' : 'transparent',
                                color: isActive ? '#FFFFFF' : '#555',
                                cursor: 'pointer',
                              }}
                            >
                              {pageNumber}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
